
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, RefreshCw, Grid, Table } from "lucide-react";
import { ClientStatusDialog } from "@/components/client-status/ClientStatusDialog";
import { ClientStatusGrid } from "@/components/client-status/ClientStatusGrid";
import { ClientStatusTable } from "@/components/client-status/ClientStatusTable";
import { useClientStatuses } from "@/hooks/useClientStatuses";
import { ClientStatus } from "@/types/clientStatus";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ClientStatusManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<ClientStatus | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const { data: statuses = [], isLoading, error, refetch } = useClientStatuses();

  // Auto-refresh every 5 seconds to catch updates
  useEffect(() => {
    const interval = setInterval(() => {
      console.log("Auto-refreshing client statuses...");
      refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [refetch]);

  const filteredStatuses = statuses.filter(status =>
    status.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) ||
    status.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
    status.status_key.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddStatus = () => {
    setSelectedStatus(undefined);
    setIsDialogOpen(true);
  };

  const handleEditStatus = (status: ClientStatus) => {
    setSelectedStatus(status);
    setIsDialogOpen(true);
  };

  const handleDialogSuccess = () => {
    setIsDialogOpen(false);
    setSelectedStatus(undefined);
    // Force refresh after success
    setTimeout(() => {
      console.log("Force refreshing after dialog success...");
      refetch();
    }, 500);
  };

  const handleManualRefresh = async () => {
    console.log("Manual refresh triggered...");
    toast.info("جاري تحديث البيانات...");
    try {
      await refetch();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6" dir="rtl">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 space-y-6" dir="rtl">
        <div className="flex justify-center items-center h-64 flex-col space-y-4">
          <div className="text-lg text-red-600">حدث خطأ في تحميل البيانات</div>
          <Button onClick={handleManualRefresh} variant="outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header with controls */}
      <div className="flex items-center gap-2">
        {/* زر الإضافة */}
        <Button onClick={handleAddStatus}>
          <Plus className="w-4 h-4" />
          إضافة حالة جديدة
        </Button>
        
        {/* الفلتر المتقدم */}
        <div className="flex items-center gap-2">
          <Button onClick={handleManualRefresh} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        
        {/* أزرار العرض */}
        <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'grid' | 'table')} className="w-auto">
          <TabsList>
            <TabsTrigger value="grid" className="flex items-center gap-2">
              <Grid className="w-4 h-4" />
              شبكة
            </TabsTrigger>
            <TabsTrigger value="table" className="flex items-center gap-2">
              <Table className="w-4 h-4" />
              جدول
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* البحث السريع */}
        <div className="relative flex-1 mr-auto">
          <Input
            placeholder="البحث في الحالات..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-10"
          />
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">

          {filteredStatuses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchTerm ? "لا توجد حالات تطابق البحث" : "لا توجد حالات مسجلة"}
            </div>
          ) : viewMode === 'grid' ? (
            <ClientStatusGrid 
              statuses={filteredStatuses} 
              onEdit={handleEditStatus}
            />
          ) : (
            <ClientStatusTable 
              statuses={filteredStatuses} 
              onEdit={handleEditStatus}
            />
          )}
        </CardContent>
      </Card>

      <ClientStatusDialog
        status={selectedStatus}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={handleDialogSuccess}
      />
    </div>
  );
}
